import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Card, Slide, useTheme, Grow, Box, alpha } from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";

/**
 * PWA Install Prompt Component
 * Displays a modern, visually appealing prompt to install the app when installation is available
 * @returns {JSX.Element|null} The PWA install prompt component or null if not shown
 * @example
 * <PWAInstallPrompt />
 */
function PWAInstallPrompt() {
  const theme = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Check if user has dismissed the prompt before
      const dismissed = localStorage.getItem("pwa-install-dismissed");
      const dismissedTime = localStorage.getItem("pwa-install-dismissed-time");

      // Show prompt if never dismissed or dismissed more than 7 days ago
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      if (!dismissed || (dismissedTime && parseInt(dismissedTime, 10) < sevenDaysAgo)) {
        setShowPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      console.log("PWA was installed");
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.removeItem("pwa-install-dismissed");
      localStorage.removeItem("pwa-install-dismissed-time");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("pwa-install-dismissed", "true");
    localStorage.setItem("pwa-install-dismissed-time", Date.now().toString());
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit timeout={600}>
      <MDBox
        sx={{
          position: "fixed",
          bottom: { xs: 16, md: 24 },
          left: { xs: 16, md: "50%" },
          transform: { xs: "none", md: "translateX(-50%)" },
          zIndex: 9999,
          width: { xs: "calc(100% - 32px)", sm: "auto" },
          maxWidth: { xs: "none", sm: 480, md: 520 },
        }}
      >
        <Grow in={showPrompt} timeout={800}>
          <Card
            sx={{
              p: { xs: 2.5, md: 3 },
              backgroundColor: theme.palette.background.card,
              backdropFilter: "blur(4px)",
              opacity: isHovered ? 1 : 0.75,
              boxShadow: ` 0 4px 32px 0 ${alpha(theme.palette.info.light, 0.3)}`,
              border: `4px solid ${alpha(theme.palette.info.light, 0.3)}`,
              position: "relative",
              overflow: "visible",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Header with app icon and title */}
            <MDBox display="flex" alignItems="center" gap={3} mb={3}>
              <Box
                sx={{
                  position: "relative",
                  width: 64,
                  height: 64,
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 8px 24px ${theme.palette.info.main}40`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05) rotate(5deg)",
                  },
                }}
              >
                <GetAppIcon
                  sx={{
                    color: theme.palette.common.white,
                    fontSize: 32,
                    transition: "all 0.3s ease",
                  }}
                />
              </Box>

              <MDBox flex={1}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  color="info"
                  sx={{
                    mb: 0.5,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                    backgroundClip: "text",
                    textFillColor: "transparent",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Install Askuala Link
                </MDTypography>
                <MDTypography
                  variant="body2"
                  color="text"
                  sx={{
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    lineHeight: 1.4,
                  }}
                >
                  Get the best experience with our progressive web app
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Action buttons */}
            <MDBox display="flex" gap={2} justifyContent="flex-end">
              <MDButton variant="outlined" color="secondary" size="medium" onClick={handleDismiss}>
                Maybe later
              </MDButton>

              <MDButton
                variant="contained"
                color="info"
                size="medium"
                onClick={handleInstallClick}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                  boxShadow: `0 4px 16px ${theme.palette.info.main}40`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.info.dark}, ${theme.palette.info.main})`,
                    boxShadow: `0 6px 20px ${theme.palette.info.main}60`,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Install App
              </MDButton>
            </MDBox>
          </Card>
        </Grow>
      </MDBox>
    </Slide>
  );
}

export default PWAInstallPrompt;
